import * as T from 'three';
import type { Traversal } from './types';

/**
 * Cheap analytic camera occlusion against the large, solid world pieces only: trunks (vertical
 * capsules), round decks (slabs), bridge walkways (oriented boxes) and big boughs. Thin things
 * (railings, signs, foliage, ordinary branches) are handled by the see-through dither instead,
 * so the camera does not pump in and out every time a rope passes behind the monkey.
 *
 * Returns the fraction of the look→desired segment that is clear (1 = unobstructed).
 */
export function clearFraction(objects: Traversal[], look: T.Vector3, desired: T.Vector3) {
    const d = desired.clone().sub(look);
    let best = 1;
    for (const o of objects) {
        let t = 1;
        if (o.type === 5) t = verticalCylinder(look, d, o.a, o.b, o.radius + .4);
        else if (o.type === 0 && o.art === 'bridge') t = bridgeBox(look, d, o);
        else if (o.type === 0) t = slab(look, d, o.a, o.radius + .25, o.a[1] - .55, o.a[1] + .12);
        else if ((o.type === 1 || o.type === 7) && o.radius >= .4) t = capsule(look, d, o.a, o.b, o.radius + .25);
        if (t < best) best = t;
    }
    return best;
}

function verticalCylinder(p: T.Vector3, d: T.Vector3, a: number[], b: number[], r: number) {
    const ox = p.x - a[0], oz = p.z - a[2];
    const A = d.x * d.x + d.z * d.z, B = 2 * (ox * d.x + oz * d.z), C = ox * ox + oz * oz - r * r;
    if (C < 0) return 1; // Look point already hugging the trunk: never trap the camera inside.
    if (A < 1e-8) return 1;
    const disc = B * B - 4 * A * C;
    if (disc < 0) return 1;
    const t = (-B - Math.sqrt(disc)) / (2 * A);
    if (t < 0 || t > 1) return 1;
    const y = p.y + d.y * t;
    return y > Math.min(a[1], b[1]) && y < Math.max(a[1], b[1]) ? t : 1;
}

/** Horizontal disc slab (round deck) between y0 and y1. */
function slab(p: T.Vector3, d: T.Vector3, c: number[], r: number, y0: number, y1: number) {
    const inside = (t: number) => { const x = p.x + d.x * t - c[0], z = p.z + d.z * t - c[2]; return x * x + z * z < r * r; };
    if (p.y > y0 && p.y < y1) return 1;
    if (Math.abs(d.y) < 1e-6) return 1;
    // Entry through the top or bottom face.
    const t = ((d.y > 0 ? y0 : y1) - p.y) / d.y;
    if (t >= 0 && t <= 1 && inside(t)) return t;
    // Entry through the rim.
    const ox = p.x - c[0], oz = p.z - c[2], A = d.x * d.x + d.z * d.z, B = 2 * (ox * d.x + oz * d.z), C = ox * ox + oz * oz - r * r;
    const disc = B * B - 4 * A * C;
    if (A < 1e-8 || disc < 0 || C < 0) return 1;
    const tr = (-B - Math.sqrt(disc)) / (2 * A), y = p.y + d.y * tr;
    return tr >= 0 && tr <= 1 && y > y0 && y < y1 ? tr : 1;
}

/** Bridge walkway as an oriented box along a→b, 2.3 m wide, planks just below the line. */
function bridgeBox(p: T.Vector3, d: T.Vector3, o: Traversal) {
    const ax = o.b[0] - o.a[0], ay = o.b[1] - o.a[1], az = o.b[2] - o.a[2];
    const len = Math.hypot(ax, ay, az);
    if (len < .01) return 1;
    const u = new T.Vector3(ax / len, ay / len, az / len);
    const side = new T.Vector3(u.z, 0, -u.x).normalize();
    const up = new T.Vector3().crossVectors(u, side).normalize();
    if (up.y < 0) up.negate();
    const rel = p.clone().sub(new T.Vector3(...o.a));
    const origin = [rel.dot(u), rel.dot(side), rel.dot(up)], dir = [d.dot(u), d.dot(side), d.dot(up)];
    const min = [0, -1.15, -.38], max = [len, 1.15, .08];
    let t0 = 0, t1 = 1;
    for (let i = 0; i < 3; i++) {
        if (Math.abs(dir[i]) < 1e-8) {
            if (origin[i] < min[i] || origin[i] > max[i]) return 1;
            continue;
        }
        let ta = (min[i] - origin[i]) / dir[i], tb = (max[i] - origin[i]) / dir[i];
        if (ta > tb) [ta, tb] = [tb, ta];
        t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
        if (t0 > t1) return 1;
    }
    return t0 > 0 ? t0 : 1; // Starting inside (standing on it) is not an occlusion.
}

function capsule(p: T.Vector3, d: T.Vector3, a: number[], b: number[], r: number) {
    // Sample conservatively; big boughs are few, so eight probes are plenty.
    const A = new T.Vector3(...a), B = new T.Vector3(...b), seg = new T.Line3(A, B), q = new T.Vector3(), c = new T.Vector3();
    if (seg.closestPointToPoint(p, true, c).distanceTo(p) < r) return 1;
    for (let i = 1; i <= 8; i++) {
        const t = i / 8;
        q.copy(p).addScaledVector(d, t);
        if (seg.closestPointToPoint(q, true, c).distanceTo(q) < r) return Math.max(0, t - .125);
    }
    return 1;
}
